import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IAchievementWithProgress, IGamificationSummary, INewlyCompleted } from 'modules/core/types/gamification';
import { fetchAchievementsThunk, fetchSummaryThunk, trackActivityThunk } from 'actions/gamificationActions';

export type GamificationState = {
  achievements: IAchievementWithProgress[];
  summary: IGamificationSummary | null;
  newlyCompleted: INewlyCompleted[];
  loading: boolean;
  hasFetchedAchievements: boolean;
  error: string | null;
  trackedSearchSources: Record<string, boolean>;
};

const initialState: GamificationState = {
  achievements: [],
  summary: null,
  newlyCompleted: [],
  loading: false,
  hasFetchedAchievements: false,
  error: null,
  trackedSearchSources: {},
};

export const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    shiftNewlyCompleted: (state) => {
      state.newlyCompleted = state.newlyCompleted.slice(1);
    },
    markSearchTracked: (state, { payload: source }: PayloadAction<string>) => {
      state.trackedSearchSources[source] = true;
    },
    clearSearchTracked: (state, { payload: source }: PayloadAction<string>) => {
      state.trackedSearchSources[source] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAchievementsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAchievementsThunk.fulfilled, (state, { payload }) => {
        state.achievements = payload;
        state.loading = false;
        state.hasFetchedAchievements = true;
      })
      .addCase(fetchAchievementsThunk.rejected, (state, action) => {
        state.loading = false;
        state.hasFetchedAchievements = true;
        state.error = action.error.message || 'Failed to fetch achievements';
      })

      .addCase(fetchSummaryThunk.fulfilled, (state, { payload }) => {
        state.summary = payload;
      })

      .addCase(trackActivityThunk.fulfilled, (state, { payload }) => {
        state.summary = {
          total_searches: payload.total_searches,
          total_submissions: payload.total_submissions,
          completed_count: payload.completed_count,
          total_achievements: payload.total_achievements,
          next_achievement: payload.next_achievement,
          closest_achievement: payload.closest_achievement,
        };

        if (payload.newly_completed.length > 0) {
          state.newlyCompleted = [...state.newlyCompleted, ...payload.newly_completed];
        }
      });
  },
});

export const { shiftNewlyCompleted, markSearchTracked, clearSearchTracked } = gamificationSlice.actions;
